// \src\hooks\useRssNews.ts

import {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import type {
  NewsItem,
  RssSource,
} from "@/context/AdminContext";
import {
  fetchAndParseFeeds,
  getCachedNews,
  setCachedNews,
  clearCache,
} from "@/utils/rss";

const AUTO_REFRESH_MS = 30 * 60 * 1000;

interface UseRssNewsResult {
  news: NewsItem[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;
}

export function useRssNews(
  sources: RssSource[],
): UseRssNewsResult {
  const [news, setNews] =
    useState<NewsItem[]>(
      () =>
        getCachedNews(
          sources,
          true,
        ) || [],
    );

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState<string | null>(null);

  const [
    lastUpdated,
    setLastUpdated,
  ] = useState<Date | null>(
    () =>
      getCachedNews(
        sources,
        true,
      )
        ? new Date()
        : null,
  );

  const [
    refreshKey,
    setRefreshKey,
  ] = useState(0);

  const requestIdRef =
    useRef(0);

  const load = useCallback(
    async () => {
      const requestId =
        ++requestIdRef.current;

      const cached =
        getCachedNews(
          sources,
          true,
        );

      if (cached) {
        setNews(cached);
        setLastUpdated(
          new Date(),
        );
      } else {
        setNews([]);
      }

      setLoading(true);
      setError(null);

      try {
        const items =
          await fetchAndParseFeeds(
            sources,
            (partialItems) => {
              if (
                requestId !==
                requestIdRef.current
              ) {
                return;
              }

              setNews(
                partialItems,
              );

              setLastUpdated(
                new Date(),
              );
            },
          );

        if (
          requestId !==
          requestIdRef.current
        ) {
          return;
        }

        if (
          items.length === 0
        ) {
          if (!cached) {
            setNews([]);
            setError(
              "Não foi possível buscar as notícias agora. Tente novamente em instantes.",
            );
          }

          return;
        }

        setNews(items);
        setLastUpdated(
          new Date(),
        );
        setCachedNews(
          sources,
          items,
        );

        setLoading(false);
      } catch {
        if (
          requestId !==
            requestIdRef.current
        ) {
          return;
        }

        if (!cached) {
          setError(
            "Não foi possível buscar as notícias agora. Tente novamente em instantes.",
          );
        }
      } finally {
        if (
          requestId ===
            requestIdRef.current
        ) {
          setLoading(false);
        }
      }
    },
    [sources],
  );

  useEffect(() => {
    void load();

    const intervalId = window.setInterval(
      () => {
        void load();
      },
      AUTO_REFRESH_MS,
    );

    return () => {
      window.clearInterval(intervalId);
      requestIdRef.current += 1;
    };
  }, [load, refreshKey]);

  const refresh =
    useCallback(() => {
      clearCache();
      setRefreshKey(
        (key) => key + 1,
      );
    }, []);

  return {
    news,
    loading,
    error,
    lastUpdated,
    refresh,
  };
}
